import { createContext, useContext, useEffect, useState } from "react";
import { EndPointService } from "@firatoltulu/smartform";

/**
 * Uç adresini yeniden yönlendirme bağlamı — kartlar aynı kalıp başka bir uç
 * kümesinden beslensin diye. Örn. admin'deki "Birlik Özeti" ekranı frontend
 * kartlarını `/admin/TobOverview/Company*` uçlarına seçili birlikle bağlar.
 *
 * Değer: { resolveUrl: (url) => string, params: object } — verilmezse (varsayılan
 * null) kartlar kendi adreslerini olduğu gibi çağırır.
 */
export const StatisticsEndpointContext = createContext(null);

/**
 * İstatistik uçlarından veri çeker.
 *
 * Her kart kendi hook'unu çağırır — biri yavaşladığında ya da hata verdiğinde
 * diğerleri etkilenmez (handoff: "her kart bağımsız iskelet gösterebilmeli").
 *
 * @param {string} url örn. "/Statistics/Summary"
 * @param {*} fallback yükleme/hata durumunda dönecek değer
 * @param {object} params sorgu parametreleri
 * @returns {{ data: *, loading: boolean, error: boolean, reload: () => void }}
 */
export default function useStatistics(url, fallback = null, params = undefined) {
  const endpoint = useContext(StatisticsEndpointContext);
  const requestUrl = endpoint && endpoint.resolveUrl ? endpoint.resolveUrl(url) : url;
  const requestParams =
    endpoint && endpoint.params ? { ...endpoint.params, ...(params || {}) } : params;

  const [data, setData] = useState(fallback);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  // Nesne referansı her render'da değiştiği için bağımlılığa serileştirilmiş hali konur
  const paramKey = requestParams ? JSON.stringify(requestParams) : "";

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError(false);

    new EndPointService()
      .get(requestUrl, requestParams)
      .then((result) => {
        if (cancelled) return;
        setData(result ?? fallback);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestUrl, paramKey, reloadToken]);

  return {
    data,
    loading,
    error,
    reload: () => setReloadToken((token) => token + 1),
  };
}
